import React, { useState } from 'react';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Settings, Users, Trash2, Copy, Shield, Clock, FileText, AlertTriangle, MessageSquare, Mic, CheckCircle } from 'lucide-react';

const CampaignSettings = ({ campaign, characters = [], onUpdate }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isSaving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Form state
  const [campaignName, setCampaignName] = useState(campaign?.name || '');
  const [campaignDescription, setCampaignDescription] = useState(campaign?.description || '');
  const [gmNotes, setGmNotes] = useState(campaign?.gmNotes || '');
  const [autoArchiveEnabled, setAutoArchiveEnabled] = useState(campaign?.settings?.autoArchiveNotes ?? true);
  const [playersCanViewSheets, setPlayersCanViewSheets] = useState(campaign?.settings?.playersCanViewSheets ?? true);

  // Discord Integration state
  const [discordSettings, setDiscordSettings] = useState(campaign?.discordSettings || {});
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const isGM = campaign?.gmId === user?.uid;

  // Save campaign metadata
  const handleSaveCampaignInfo = async () => {
    if (!isGM) {
      toast.error('Only the GM can modify campaign settings');
      return;
    }

    if (!campaignName.trim()) {
      toast.error('Campaign name is required');
      return;
    }

    setSaving(true);
    try {
      const campaignRef = doc(db, 'campaigns', campaign.id);
      await updateDoc(campaignRef, {
        name: campaignName.trim(),
        description: campaignDescription.trim(),
        gmNotes: gmNotes.trim(),
        updatedAt: new Date()
      });

      toast.success('Campaign information updated');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating campaign:', error);
      toast.error('Failed to update campaign');
    } finally {
      setSaving(false);
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    if (!isGM) {
      toast.error('Only the GM can modify campaign settings');
      return;
    }

    setSaving(true);
    try {
      const campaignRef = doc(db, 'campaigns', campaign.id);
      await updateDoc(campaignRef, {
        settings: {
          autoArchiveNotes: autoArchiveEnabled,
          playersCanViewSheets: playersCanViewSheets
        },
        updatedAt: new Date()
      });

      toast.success('Settings updated');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  // Remove player from campaign
  const handleRemovePlayer = async (characterId) => {
    if (!isGM) {
      toast.error('Only the GM can remove players');
      return;
    }

    if (!window.confirm('Are you sure you want to remove this player from the campaign?')) {
      return;
    }

    try {
      const campaignRef = doc(db, 'campaigns', campaign.id);
      const updatedPlayers = campaign.players.filter(p => p.characterId !== characterId);

      await updateDoc(campaignRef, {
        players: updatedPlayers,
        updatedAt: new Date()
      });

      toast.success('Player removed from campaign');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error removing player:', error);
      toast.error('Failed to remove player');
    }
  };

  // Copy invite link
  const handleCopyInviteLink = () => {
    const inviteUrl = `${window.location.origin}/campaign/${campaign.id}/join`;
    navigator.clipboard.writeText(inviteUrl);
    toast.success('Invite link copied to clipboard');
  };

  // Handle Discord disconnect
  const handleDisconnectDiscord = async () => {
    if (!isGM) {
      toast.error('Only the GM can disconnect Discord');
      return;
    }

    try {
      const campaignRef = doc(db, 'campaigns', campaign.id);
      await updateDoc(campaignRef, {
        discordSettings: null,
        updatedAt: new Date()
      });

      setDiscordSettings({});
      setShowDisconnectConfirm(false);
      toast.success('Discord disconnected. You can now link a different server.');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error disconnecting Discord:', error);
      toast.error('Failed to disconnect Discord');
    }
  };

  // Toggle voice transcription
  const handleToggleTranscription = async () => {
    if (!isGM) return;
    try {
      const newValue = !discordSettings.enableTranscription;
      const campaignRef = doc(db, 'campaigns', campaign.id);
      await updateDoc(campaignRef, {
        'discordSettings.enableTranscription': newValue,
        updatedAt: new Date()
      });
      setDiscordSettings(prev => ({ ...prev, enableTranscription: newValue }));
      toast.success(newValue ? 'Voice transcription enabled' : 'Voice transcription disabled');
    } catch (error) {
      console.error('Error toggling transcription:', error);
      toast.error('Failed to update setting');
    }
  };

  // Delete campaign
  const handleDeleteCampaign = async () => {
    if (!isGM) {
      toast.error('Only the GM can delete the campaign');
      return;
    }

    if (deleteConfirmText !== campaign.name) {
      toast.error('Campaign name does not match');
      return;
    }

    try {
      const campaignRef = doc(db, 'campaigns', campaign.id);
      await deleteDoc(campaignRef);

      toast.success('Campaign deleted');
      navigate('/campaigns');
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Failed to delete campaign');
    }
  };

  if (!isGM) {
    return (
      <div className="p-4 lg:p-8">
        <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-700 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-red-400" />
            <h3 className="text-lg font-semibold text-gray-100">GM Only</h3>
          </div>
          <p className="text-gray-400">Only the Game Master can access campaign settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 space-y-6">
      {/* Campaign Information */}
      <div className="bg-gradient-to-br from-gray-900 to-black border border-red-700 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Settings className="w-6 h-6 text-red-400" />
          <h3 className="text-xl font-bold text-red-400 font-serif">Campaign Information</h3>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Campaign Name *
            </label>
            <input
              type="text"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              className="w-full px-3 py-2 bg-[#1E1E1E] border border-gray-600 rounded text-gray-100 focus:outline-none focus:border-red-500"
              placeholder="Enter campaign name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Campaign Description
            </label>
            <textarea
              value={campaignDescription}
              onChange={(e) => setCampaignDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-[#1E1E1E] border border-gray-600 rounded text-gray-100 focus:outline-none focus:border-red-500 resize-none"
              placeholder="What's your campaign about?"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                GM Notes (Private)
              </div>
            </label>
            <textarea
              value={gmNotes}
              onChange={(e) => setGmNotes(e.target.value)}
              rows={5}
              className="w-full px-3 py-2 bg-[#1E1E1E] border border-gray-600 rounded text-gray-100 focus:outline-none focus:border-red-500 resize-none"
              placeholder="Private notes only you can see..."
            />
            <p className="text-xs text-gray-500 mt-1">Only the GM can see these notes</p>
          </div>

          <button
            onClick={handleSaveCampaignInfo}
            disabled={isSaving}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white rounded transition-colors"
          >
            {isSaving ? 'Saving...' : 'Save Campaign Info'}
          </button>
        </div>
      </div>

      {/* Session & Gameplay Settings */}
      <div className="bg-gradient-to-br from-gray-900 to-black border border-red-700 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Clock className="w-6 h-6 text-red-400" />
          <h3 className="text-xl font-bold text-red-400 font-serif">Session & Gameplay Settings</h3>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-[#1E1E1E] rounded border border-gray-700">
            <div>
              <div className="font-medium text-gray-100">Auto-Archive Session Notes</div>
              <div className="text-sm text-gray-400">Automatically archive notes when starting a new session</div>
            </div>
            <button
              onClick={() => setAutoArchiveEnabled(!autoArchiveEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                autoArchiveEnabled ? 'bg-green-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  autoArchiveEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between p-3 bg-[#1E1E1E] rounded border border-gray-700">
            <div>
              <div className="font-medium text-gray-100">Players Can View Each Other's Sheets</div>
              <div className="text-sm text-gray-400">Allow players to view party member character sheets</div>
            </div>
            <button
              onClick={() => setPlayersCanViewSheets(!playersCanViewSheets)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                playersCanViewSheets ? 'bg-green-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  playersCanViewSheets ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <button
            onClick={handleSaveSettings}
            disabled={isSaving}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white rounded transition-colors"
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Player Management */}
      <div className="bg-gradient-to-br from-gray-900 to-black border border-red-700 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Users className="w-6 h-6 text-red-400" />
          <h3 className="text-xl font-bold text-red-400 font-serif">Player Management</h3>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyInviteLink}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
            >
              <Copy className="w-4 h-4" />
              Copy Invite Link
            </button>
          </div>

          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-semibold text-gray-300 mb-3">Current Players ({characters.length})</h4>
            <div className="space-y-2">
              {characters.map(character => {
                const player = campaign.players.find(p => p.characterId === character.id);
                const isGMCharacter = character.userId === campaign.gmId;

                return (
                  <div
                    key={character.id}
                    className="flex items-center justify-between p-3 bg-[#1E1E1E] rounded border border-gray-700"
                  >
                    <div>
                      <div className="font-medium text-gray-100">
                        {character.name}
                        {isGMCharacter && (
                          <span className="ml-2 text-xs text-purple-400 font-semibold">(GM)</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-400">
                        Level {character.level || 1} {character.class?.charAt(0).toUpperCase() + character.class?.slice(1)}
                      </div>
                    </div>
                    {!isGMCharacter && (
                      <button
                        onClick={() => handleRemovePlayer(character.id)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                        title="Remove player"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}

              {characters.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  No players in campaign
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Discord Integration */}
      <div id="discord-integration" className="bg-gradient-to-br from-gray-900 to-black border border-purple-700 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <MessageSquare className="w-6 h-6 text-purple-400" />
          <h3 className="text-xl font-bold text-purple-400 font-serif">Discord Integration</h3>
        </div>

        {!discordSettings?.linked ? (
          <div className="space-y-4">
            <p className="text-gray-300">
              Connect your Discord server to enable voice transcription, session notifications, and bot commands.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-[#1E1E1E] rounded border border-gray-700">
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-purple-300">Features:</h4>
                <ul className="text-sm text-gray-400 space-y-1">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-green-400" />
                    Voice transcription with VAD
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-green-400" />
                    Session notifications
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-green-400" />
                    Party status commands
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-green-400" />
                    Secret DC management
                  </li>
                </ul>
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-purple-300">Setup:</h4>
                <ol className="text-sm text-gray-400 space-y-1">
                  <li>1. Invite bot to your server</li>
                  <li>2. Run <code className="bg-gray-800 px-1 rounded">/link {campaign.id}</code></li>
                  <li>3. Select channels for notifications</li>
                  <li>4. Start recording sessions!</li>
                </ol>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => window.open(`https://discord.com/api/oauth2/authorize?client_id=${process.env.REACT_APP_DISCORD_CLIENT_ID || 'YOUR_CLIENT_ID'}&permissions=3235846&integration_type=0&scope=bot+applications.commands`, '_blank')}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
              >
                <MessageSquare className="w-4 h-4" />
                Invite Chaggerheart Bot
              </button>
            </div>

            <div className="p-3 bg-blue-900/20 border border-blue-700 rounded">
              <p className="text-sm text-blue-300">
                <strong>Campaign ID:</strong> <code className="bg-gray-800 px-2 py-1 rounded ml-2">{campaign.id}</code>
              </p>
              <p className="text-xs text-gray-400 mt-1">Use this ID when running /link in Discord</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-[#1E1E1E] rounded border border-green-700">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="font-medium text-green-400">Connected</span>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-300">Server:</span>
                  <span className="text-gray-100 font-medium">{discordSettings.guildName || 'Unknown'}</span>
                </div>

                {discordSettings.textChannelName && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-300 ml-6">Text:</span>
                    <span className="text-gray-100">#{discordSettings.textChannelName}</span>
                  </div>
                )}

                {discordSettings.voiceChannelName && (
                  <div className="flex items-center gap-2">
                    <Mic className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-300">Voice:</span>
                    <span className="text-gray-100">#{discordSettings.voiceChannelName}</span>
                  </div>
                )}

                <div className="flex items-center gap-2 pt-2">
                  <span className="text-xs text-gray-500">
                    Linked {discordSettings.linkedAt ? new Date(discordSettings.linkedAt).toLocaleDateString() : 'recently'}
                  </span>
                </div>
              </div>

              {/* Disconnect / Re-link */}
              {!showDisconnectConfirm ? (
                <button
                  onClick={() => setShowDisconnectConfirm(true)}
                  className="mt-3 px-3 py-1.5 text-sm text-red-400 border border-red-700/50 rounded hover:bg-red-900/30 transition-colors"
                >
                  Disconnect & Re-link to Different Server
                </button>
              ) : (
                <div className="mt-3 p-3 bg-red-900/20 border border-red-700 rounded space-y-2">
                  <p className="text-sm text-gray-300">
                    This will unlink <strong className="text-red-400">{discordSettings.guildName}</strong>. You can then link a different server.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleDisconnectDiscord}
                      className="px-3 py-1.5 text-sm bg-red-900 hover:bg-red-800 text-white rounded transition-colors"
                    >
                      Disconnect
                    </button>
                    <button
                      onClick={() => setShowDisconnectConfirm(false)}
                      className="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 text-gray-200 rounded transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Voice Transcription Toggle */}
            <div className="flex items-center justify-between p-3 bg-[#1E1E1E] rounded border border-gray-700">
              <div>
                <div className="font-medium text-gray-100">Voice Transcription</div>
                <div className="text-sm text-gray-400">Transcribe voice sessions using Deepgram + VAD</div>
              </div>
              <button
                onClick={handleToggleTranscription}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  discordSettings.enableTranscription ? 'bg-purple-600' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    discordSettings.enableTranscription ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Slash Commands Reference */}
            <div className="p-4 bg-purple-900/20 border border-purple-700 rounded space-y-3">
              <h4 className="text-sm font-semibold text-purple-300">Discord Bot Commands</h4>

              <div className="space-y-2 text-sm">
                <div className="space-y-1">
                  <div className="text-gray-200 font-medium">Setup</div>
                  <div className="text-xs text-gray-400 font-mono space-y-0.5">
                    <div><span className="text-purple-300">/link</span> <span className="text-gray-500">&lt;campaign_id&gt;</span> - Link this Discord server to your campaign</div>
                    <div><span className="text-purple-300">/link-character</span> - Link your Discord account to your character (for speaker ID in transcripts)</div>
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="text-gray-200 font-medium">Recording</div>
                  <div className="text-xs text-gray-400 font-mono space-y-0.5">
                    <div><span className="text-purple-300">/record start</span> - Begin voice recording (requires consent from all participants)</div>
                    <div><span className="text-purple-300">/record stop</span> - Stop recording and process the audio</div>
                    <div><span className="text-purple-300">/record status</span> - Check current recording status</div>
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="text-gray-200 font-medium">Sessions</div>
                  <div className="text-xs text-gray-400 font-mono space-y-0.5">
                    <div><span className="text-purple-300">/session start</span> - Start a new game session</div>
                    <div><span className="text-purple-300">/session end</span> - End the current session</div>
                    <div><span className="text-purple-300">/session pause</span> / <span className="text-purple-300">resume</span> - Pause or resume the session timer</div>
                    <div><span className="text-purple-300">/session status</span> - View current session info</div>
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="text-gray-200 font-medium">Party</div>
                  <div className="text-xs text-gray-400 font-mono space-y-0.5">
                    <div><span className="text-purple-300">/party</span> - Show party HP, Stress, and Hope status</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Danger Zone */}
      <div className="bg-gradient-to-br from-gray-900 to-black border border-red-900 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-red-500" />
          <h3 className="text-xl font-bold text-red-500 font-serif">Danger Zone</h3>
        </div>

        {!showDeleteConfirm ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2 bg-red-900 hover:bg-red-800 text-white rounded transition-colors"
          >
            Delete Campaign
          </button>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-300">
              This action cannot be undone. This will permanently delete the campaign and all associated data.
            </p>
            <p className="text-sm text-gray-300">
              Type <strong className="text-red-400">{campaign.name}</strong> to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="w-full px-3 py-2 bg-[#1E1E1E] border border-red-700 rounded text-gray-100 focus:outline-none focus:border-red-500"
              placeholder="Type campaign name"
            />
            <div className="flex gap-2">
              <button
                onClick={handleDeleteCampaign}
                disabled={deleteConfirmText !== campaign.name}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white rounded transition-colors"
              >
                I understand, delete this campaign
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignSettings;
